import { useState, useEffect, useRef } from "react";
import { createFilterOptions, getId, isValidId, updateActiveTask, Util } from '../../util/Util'
import RefreshButton from "../../components/RefreshButton";
import { useMove } from "./MoveContext";
import { MIN_DATE, MYSQL_LIMITS } from "../../util/constants";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { useAuth } from "../../hooks/useAuth";
import { Autocomplete, Grid, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { businessTodayDate, parseISODay } from "../../util/businessClock";

interface Props {
	setValidateCheck: (value: boolean) => void;
}

interface Destination {
	id: number;
	name: string;
}

interface Source {
	id: number;
	name: string;
	destinations: Destination[];
}

interface OperationGroup {
	operation: {
		id: number;
		name: string;
	};
	sources: Source[];
}

const MoveDetail = ({ setValidateCheck }: Props) => {
	const [mapping, setMapping] = useState<OperationGroup[]>([]);
	const [selectedOperationId, setSelectedOperationId] = useState<number>(-1);
	const [selectedSourceId, setSelectedSourceId] = useState<number>(-1);
	const [selectedDestinationId, setSelectedDestinationId] = useState<number>(-1);
	const [clients, setClients] = useState<any[]>([]);
	const [isClientLoading, setIsClientLoading] = useState<boolean>(false);
	const [isMapLoading, setIsMapLoading] = useState<boolean>(false);
	const [errorInput, setErrorInput] = useState("");

	const { pendingTasks, setPendingTasks, activeTask, isModalOpen } = useMove();
	const { queueReRun, t } = useUI();
	const { appSettings } = useAppSocket();
	const { api } = useAuth();

	const prevTaskIdRef = useRef<string | null>(null);

	const selectedOperation = mapping.find(op => op.operation.id === selectedOperationId);
	const operations = mapping.map(op => ({
		id: op.operation.id,
		name: op.operation.name,
	}));
	const sources = selectedOperation?.sources || [];
	const selectedSource = sources.find(src => src.id === selectedSourceId);
	const destinations = selectedSource?.destinations || [];
	const itemsExist = (activeTask?.items || []).length > 0;
	const minDate = appSettings?.SYSTEM_INCEPTION_DATE?.value || MIN_DATE;

	const fetchClients = async () => {
		setIsClientLoading(true);

		const res = await api.get("/clients",
			{
				params: {
					Active: true,
					Type: "WORKER"
				}
			}
		);

		if (res) {
			if (res.status === 200) {
				res.data.sort((a: any, b: any) => a.acc_name.localeCompare(b.acc_name));
				setClients(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchClients);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsClientLoading(false);
	};

	const fetchOperations = async () => {
		setMapping([]);
		setSelectedOperationId(-1);
		setSelectedSourceId(-1);
		setSelectedDestinationId(-1);
		setIsMapLoading(true);

		const res = await api.get("/operations/maps",
			{
				params: {
					Type: "Move"
				}
			}
		);

		if (res) {
			if (res.status === 200) {
				setMapping(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchOperations);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsMapLoading(false);
	};

	const onReceiverChanged = (receiver: number) => {
		updateActiveTask(setPendingTasks, { receiver });
	};

	const onRefChanged = (reference: string = Util.getMoveNumber()) => {
		updateActiveTask(setPendingTasks, { reference });
	};

	const onDateChanged = (taskDate: Date = businessTodayDate()) => {
		updateActiveTask(setPendingTasks, { date: Util.formatISODate(taskDate) });
	};

	const onOperationChanged = (operationId: number) => {
		setSelectedOperationId(operationId);
	};

	const onSourceChanged = (sourceId: number) => {
		setSelectedSourceId(sourceId);
	};

	const onDestChanged = (destId: number) => {
		setSelectedDestinationId(destId);
	};

	useEffect(() => {
		if (isModalOpen) {
			fetchClients();
			fetchOperations();
		} else {
			setClients([]);
			setMapping([]);
			setSelectedOperationId(-1);
			setSelectedSourceId(-1);
			setSelectedDestinationId(-1);
		}
	}, [isModalOpen]);

	useEffect(() => {
		if (clients.length > 0) {
			const receiverId = getId(clients, activeTask?.receiver);
			if (receiverId !== activeTask?.receiver) {
				onReceiverChanged(receiverId);
			}
		}
	}, [activeTask, clients]);

	useEffect(() => {
		if (mapping.length > 0) {
			setSelectedOperationId(getId(operations, activeTask?.operation));
		}
	}, [mapping]);

	useEffect(() => {
		if (selectedOperationId > 0 || isNaN(selectedOperationId)) {
			if (activeTask?.operation !== selectedOperationId) {
				updateActiveTask(setPendingTasks, { operation: selectedOperationId });
			}
			setSelectedSourceId(getId(sources, activeTask?.source));
		}
	}, [selectedOperationId]);

	useEffect(() => {
		if (selectedSourceId > 0 || isNaN(selectedSourceId)) {
			if (activeTask?.source !== selectedSourceId) {
				updateActiveTask(setPendingTasks, { source: selectedSourceId });
			}
			setSelectedDestinationId(getId(destinations, activeTask?.destination));
		}
	}, [selectedSourceId]);

	useEffect(() => {
		if (selectedDestinationId > 0 || isNaN(selectedDestinationId)) {
			if (activeTask?.destination !== selectedDestinationId) {
				updateActiveTask(setPendingTasks, { destination: selectedDestinationId });
			}
		}
	}, [selectedDestinationId]);

	useEffect(() => {
		if (pendingTasks.length > 0) {
			const currentTaskId = activeTask?.id;
			if (prevTaskIdRef.current && prevTaskIdRef.current !== currentTaskId) {
				if (clients.length > 0 && (activeTask?.receiver || -1) < 1) {
					updateActiveTask(setPendingTasks, {receiver: clients[0].id});
				}

				fetchOperations();
			}
			prevTaskIdRef.current = currentTaskId || null;
		}
	}, [pendingTasks]);

	useEffect(() => {
		const isValid = isValidId(clients, activeTask?.receiver) &&
			isValidId(operations, activeTask?.operation) &&
			isValidId(sources, activeTask?.source) &&
			isValidId(destinations, activeTask?.destination);

		setValidateCheck(isValid);
	}, [activeTask, clients, sources, destinations, destinations]);

	return (
		<Grid container spacing={0}>
			<Grid size={{ xs: 4, sm: 4, md: 2 }} >
				<Autocomplete
					id="clientInput"
					size="small"
					options={clients}
					filterOptions={createFilterOptions((opt) => opt.acc_name || "")}
					getOptionLabel={(client) => client.acc_name || ""}
					value={clients.find((c) => c.id === activeTask?.receiver) || null}
					onChange={(_, newValue) => {
						onReceiverChanged(newValue?.id || -1);
					}}
					loading={isClientLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={!activeTask || isClientLoading}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t("common:moveDetail.receiver")}&nbsp;&nbsp;
									<RefreshButton isWorking={isClientLoading} onClick={fetchClients} disabled={isClientLoading} />
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: 4, sm: 4, md: 2 }} >
				<TextField
					id="referenceInput"
					size="small"
					label={
						<span>
							{t("common:supplyDetail.reference")}&nbsp;&nbsp;
							<RefreshButton isWorking={false} onClick={onRefChanged} />
						</span>
					}
					variant="filled"
					fullWidth
					type="text"
					value={activeTask?.reference || ""}
					inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
					onBlur={(e) => {
						if (!e.target.value || e.target.value.trim() === "") {
							onRefChanged();
						}
					}}
					onChange={(e) => onRefChanged(e.target.value.trim())}
					disabled={!activeTask}
				/>
			</Grid>
			<Grid size={{ xs: 4, sm: 4, md: 2 }} >
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label={
							<span>
								{t("common:ui.labels.date")}&nbsp;&nbsp;
								<RefreshButton isWorking={false} onClick={onDateChanged} />
							</span>
						}
						value={activeTask?.date ? parseISODay(activeTask?.date) as Date : businessTodayDate()}
						minDate={(parseISODay(minDate) as Date)}
						onChange={(newDate) => {
							if (!newDate || Util.compareStrings(newDate.toString(), "Invalid Date") || newDate < (parseISODay(minDate) as Date)) {
								setErrorInput("dateInput");
								setTimeout(() => setErrorInput(""), 1500);
								return;
							}
							const date = newDate instanceof Date ? newDate : newDate.toDate(); // ✅ safe narrowing
							onDateChanged(date);
						}}
						disabled={!activeTask}
						slotProps={{
							textField: {
								id: "dateInput",
								size: "small",
								fullWidth: true,
								variant: "filled", // ✅ makes the textfield filled
								error: errorInput === "dateInput", // ✅ built-in error state
								helperText:
									errorInput === "dateInput" ? t("common:validation.invalidDate") : "", // ✅ error message
							},
						}}
					/>
				</LocalizationProvider>
			</Grid>
			<Grid size={{ xs: 4, sm: 4, md: 2 }} >
				<Autocomplete
					id="operationInput"
					size="small"
					options={operations}
					filterOptions={createFilterOptions((opt) => opt.name || "")}
					getOptionLabel={(operation) => operation.name || ""}
					value={operations.find((o) => o.id === activeTask?.operation) || null}
					onChange={(_, newValue) => {
						onOperationChanged(parseInt(newValue?.id || -1));
					}}
					loading={isMapLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={isMapLoading || !activeTask}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t("common:moveDetail.type")}&nbsp;&nbsp;
									<RefreshButton isWorking={isMapLoading} onClick={fetchOperations} disabled={isMapLoading} />
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: 4, sm: 4, md: 2 }} >
				<Autocomplete
					id="sourceInput"
					size="small"
					options={sources}
					filterOptions={createFilterOptions((opt) => opt.name || "")}
					getOptionLabel={(source) => source.name || ""}
					value={sources.find((s) => s.id === activeTask?.source) || null}
					onChange={(_, newValue) => {
						onSourceChanged(newValue?.id || -1);
					}}
					loading={isMapLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={isMapLoading || sources.length === 0 || itemsExist || !activeTask}
					renderInput={(params) => (
						<TextField
							{...params}
							label={t("common:ui.labels.from")}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: 4, sm: 4, md: 2 }} >
				<Autocomplete
					id="destInput"
					size="small"
					options={destinations}
					filterOptions={createFilterOptions((opt) => opt.name || "")}
					getOptionLabel={(destination) => destination.name || ""}
					value={destinations.find((d) => d.id === activeTask?.destination) || null}
					onChange={(_, newValue) => {
						onDestChanged(newValue?.id || -1);
					}}
					loading={isMapLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={isMapLoading || destinations.length === 0 || !activeTask}
					renderInput={(params) => (
						<TextField
							{...params}
							label={t("common:ui.labels.to")}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
		</Grid>
	);
};

export default MoveDetail;

